import {
  type BaseExpandType,
  type TBaseExpandOptions,
  createExpandString,
  createExpandMeta,
} from '@modules/api-core'

// Admin expands
export type AdminExpandType =
  | BaseExpandType
  | 'region.countries'
  | 'region.territories'
  | 'distribution_details'

export type TAdminExpandOptions = TBaseExpandOptions & {
  'region.countries'?: boolean
  'region.territories'?: boolean
  distribution_details?: boolean
  client?: boolean
  history?: boolean
  pending_changes?: boolean
}

export const createAdminExpandString = (expands: TAdminExpandOptions): string => {
  return createExpandString(expands as Record<string, boolean>)
}

export const createAdminExpandMeta = (expands: TAdminExpandOptions) => {
  return createExpandMeta(expands as Record<string, boolean>)
}

export const ADMIN_EXPAND_PRESETS = {
  FULL: createAdminExpandMeta({
    songs: true,
    files: true,
    distribution_details: true,
    'region.countries': true,
    'region.territories': true,
    client: true,
    history: true,
    pending_changes: true,
  }),
  REGION: createAdminExpandMeta({ 'region.countries': true, 'region.territories': true }),
  SONGS_ONLY: createAdminExpandMeta({ songs: true }),
  FILES_ONLY: createAdminExpandMeta({ files: true }),
  DISTRIBUTION_DETAILS_ONLY: createAdminExpandMeta({ distribution_details: true }),
  SONGS_AND_FILES: createAdminExpandMeta({ songs: true, files: true }),
  DISTRIBUTION_DETAILS_AND_SONGS: createAdminExpandMeta({
    distribution_details: true,
    songs: true,
    client: true,
  }),
} as const

export type PublisherExpandType = BaseExpandType | 'approval_decision' | 'counter_offer_fees'

export type TPublisherExpandOptions = TBaseExpandOptions & {
  'songs.approval_decision'?: boolean
  'songs.counter_offer_fees'?: boolean
  'client_request.files'?: boolean
  'client_request.songs'?: boolean
  'songs.client_request_song'?: boolean
  client_request?: boolean
  'client_request.distribution_details'?: boolean
  'songs.claimed_shares'?: boolean
  'songs.submitted_copyright_notice'?: boolean
  'region.territories'?: boolean
  'region.countries'?: boolean
  'songs.claimed_shares.region.territories'?: boolean
  'songs.claimed_shares.region.countries'?: boolean
  files?: boolean
}

export const createPublisherExpandString = (expands: TPublisherExpandOptions): string => {
  return createExpandString(expands as Record<string, boolean>)
}

export const createPublisherExpandMeta = (expands: TPublisherExpandOptions) => {
  return createExpandMeta(expands as Record<string, boolean>)
}

export const PUBLISHER_EXPAND_PRESETS = {
  FULL: createPublisherExpandMeta({
    songs: true,
    'songs.approval_decision': true,
    'songs.counter_offer_fees': true,
    'client_request.files': true,
    'client_request.songs': true,
    // prettier-ignore
    'client_request': true,
    'client_request.distribution_details': true,
    'songs.client_request_song': true,
    'songs.claimed_shares': true,
    'songs.submitted_copyright_notice': true,
    'region.territories': true,
    'region.countries': true,
    'songs.claimed_shares.region.territories': true,
    'songs.claimed_shares.region.countries': true,
    files: true,
  }),
  SONGS_ONLY: createPublisherExpandMeta({ songs: true }),
  FILES_ONLY: createPublisherExpandMeta({ files: true }),
  DISTRIBUTION_DETAILS_ONLY: createPublisherExpandMeta({ distribution_details: true }),
  SONGS_AND_FILES: createPublisherExpandMeta({ songs: true, files: true }),
  DISTRIBUTION_DETAILS_AND_SONGS: createPublisherExpandMeta({
    distribution_details: true,
    songs: true,
  }),
  CLIENT_REQUEST: createPublisherExpandMeta({
    // prettier-ignore
    'client_request': true,
  }),
  SHARE_REQUEST: createPublisherExpandMeta({
    // prettier-ignore
    'client_request': true,
    'client_request.songs': true,
    'client_request.distribution_details': true,
    files: true,
  }),
} as const
